import type { TemplateResult } from 'lit';
import { html, nothing } from 'lit';
import { PageContent } from 'src/pages/page-content';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import type { AssetsPageViewModel } from './assets-page-view-model';
import './d-assets-table';
import { isEmpty } from 'lodash';
import { uuid } from 'src/utilities/text';
import type { PageLink, PageLinks } from 'src/pages/overview-page/overview-page-view-model';
import 'src/library/promo/d-promo-section';
import { features } from '../../store/selectors/features/features';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { commonFieldsByCode } from 'src/store/fields';
import type { CreateAssetInput } from '../../layout/parts/create-asset-input';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { TableDefinition } from '../../library/abstracts/table-definition';
import type { ItemTableColumn } from '../../library/abstracts/item-table-column';
import type { LabelItem } from 'src/library/abstracts/label-item';
import '../d-no-items-prompt';

@customElement('d-assets-page-content')
export class DAssetsPageContent extends PageContent<AssetsPageViewModel> {
  static readonly styles = [...super.styles];

  @property({ type: String })
  sortBy = 'name';
  @property({ type: String })
  groupBy = 'location';

  renderPageLinks(list: PageLinks) {
    if (list) {
      return html`
        ${list.items.map((pageLink: PageLink) => {
          return html`<a class="page-link" href="${pageLink.href}">${pageLink.name}</a>`;
        })}
      `;
    }
    return html``;
  }

  renderAssetsTable(table: TableDefinition) {
    if (table.items) {
      const items = this.pageView[table.items];
      let header = html``;
      if (table.field && !table.noHeader) {
        const f = commonFieldsByCode()[table.fieldForLabel];
        if (f) {
          header = html`<h2>${f.label}</h2>`;
        }
      }
      if (items.length) {
        return html` ${header}
          <d-assets-table
            .tableColumns=${table.tableColumns}
            .items=${items}
            .sortBy=${this.sortBy}
            .groupBy=${this.groupBy}
            .filter=${true}
            theme-page
            .contentStickyTop=${this.contentStickyTop}
          ></d-assets-table>`;
      }
    }
    return html``;
  }

  renderContent() {
    if (this.pageView.promoData) {
      return html` <d-promo-section
        theme-page
        .employeesCount=${this.pageView.employeesCount}
        .specialTerms=${this.pageView.specialTerms}
        .promoData=${this.pageView.promoData}
        .features=${features}
        .canUpgrade=${this.writeAccess}
      ></d-promo-section>`;
    }
    return html`
      ${this.renderPageLinks(this.pageView.pageLinks)}
      ${isEmpty(this.pageView.assetsNotDiscarded) && isEmpty(this.pageView.assetsDiscarded)
        ? html` <d-no-items-prompt
            entity-type="assets"
            heading="${t('assets.noAssetsHeading')}"
            prompt="${t('assets.noAssetsPrompt')}"
            .writeAccess=${this.pageView.writeAccess}
            @click=${() => this.onCreateAsset()}
          ></d-no-items-prompt>`
        : html`
            ${this.tables(this.pageView).map((table) => {
              return html`${this.renderAssetsTable(table)}`;
            })}
          `}
    `;
  }

  protected tables(view: any): TableDefinition[] {
    return [
      {
        field: 'assetsNotDiscarded',
        fieldForLabel: 'organization_assetsNotDiscarded',
        noHeader: true,
        type: 'assets',
        items: 'assetsNotDiscarded',
        tableColumns: this.assetsTableColumns(view, view.assetsNotDiscardedHasCheckedEvents),
        sortBy: this.sortBy,
        groupBy: this.groupBy,
      },
      {
        field: 'assetsDiscarded',
        fieldForLabel: 'organization_assetsDiscarded',
        type: 'assets',
        items: 'assetsDiscarded',
        tableColumns: this.assetsTableColumns(view, view.assetsDiscardedHasCheckedEvents),
        sortBy: this.sortBy,
        groupBy: this.groupBy,
        excludeFromPfd: true,
      },
    ];
  }

  protected renderCustomActions(): TemplateResult | typeof nothing {
    return html`
      <d-action href="${this.pageView.pdfHref}" id="print" blank>PDF</d-action>
      ${this.singleUserVersion
        ? nothing
        : html`<d-action class="action" @click=${() => this.onShareContent()}>${t('content.share')}</d-action>`}
    `;
  }

  private assetsTableColumns(view: AssetsPageViewModel, hasCheckedEvents: boolean): ItemTableColumn[] {
    let eventLegends: LabelItem[] | undefined;
    if (hasCheckedEvents) {
      eventLegends = [
        {
          classes: 'legends',
          items: [
            { text: t('assets.legendDone'), classes: 'legend done' },
            { text: t('assets.legendOk'), classes: 'legend ok' },
            { text: t('assets.legendNotOk'), classes: 'legend not-ok' },
          ],
        },
      ];
    }
    return [
      {
        name: 'name',
        field: 'name',
        label: t('assets.columnName'),
        sortOption: true,
      },
      {
        name: 'number',
        field: 'number',
        label: t('assets.columnId'),
        sortOption: true,
      },
      {
        name: 'category',
        field: 'category',
        label: t('assets.columnCategory'),
        sortOption: true,
        groupOption: true,
      },
      {
        name: 'location',
        field: 'location',
        label: t('assets.columnLocation'),
        groupOption: true,
      },
      {
        name: 'registered',
        field: 'registered',
        label: t('assets.columnRegistered'),
        sortOption: true,
        hide: true,
        excludeFromPfd: true,
      },
      {
        name: 'requiresElectricity',
        field: 'requiresElectricity',
        label: t('assets.columnEmu'),
        type: 'yesIfTrue',
        condition: !view.variantSimplifiedAssets,
      },
      {
        name: 'tasksAndEvents',
        field: 'tasksAndEvents',
        label: t('assets.tasksHeader'),
        noValue: t('assets.noTasks'),
        labelItems: eventLegends,
        excludeFromPfd: true,
      },
    ];
  }

  private onCreateAsset() {
    if (this.pageView.writeAccess) {
      const id = uuid();
      const input: CreateAssetInput = {
        entityType: 'assets',
        entityUuid: id,
        targetUrl: '/account/' + this.pageView.organizationId + '/' + pageIds.assets + '/assets/' + id + '?edit',
      };
      this.dispatchEvent(
        new CustomEvent<CreateEntityInput>('create-entity', {
          bubbles: true,
          composed: true,
          detail: input,
        }),
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-assets-page-content': DAssetsPageContent;
  }
}
